import { useCallback, useEffect, useRef, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ServiceCollapsibleSection } from "./ServiceCollapsibleSection";
import { ghostBtnClass } from "./serviceShared";
import {
  deleteServiceOfferingDocument,
  formatOfferingDocumentSize,
  listServiceOfferingDocuments,
  offeringDocumentLinkLabel,
  offeringDocumentPublisherSuffix,
  SERVICE_OFFERING_DOCUMENT_ACCEPT,
  SERVICE_OFFERING_DOCUMENT_KIND_LABELS,
  uploadServiceOfferingDocument,
} from "@/lib/service/offeringDocumentsClient";
import type { ServiceOfferingDocument, ServiceOfferingDocumentKind } from "@/lib/service/types";

const UPLOAD_KINDS: ServiceOfferingDocumentKind[] = [
  "order_form",
  "pricing_worksheet",
  "guidance",
  "other",
];

export function ServiceOfferingDocumentsSection(props: {
  offeringId: string;
  offeringLabel: string;
  readOnly?: boolean;
  presentation?: "collapsible" | "card";
}) {
  const { offeringId, offeringLabel, readOnly = false, presentation = "collapsible" } = props;
  const [documents, setDocuments] = useState<ServiceOfferingDocument[]>([]);
  const [isInherited, setIsInherited] = useState(false);
  const [publisherOrgName, setPublisherOrgName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKind, setBusyKind] = useState<ServiceOfferingDocumentKind | null>(null);
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null);
  const fileInputs = useRef<Partial<Record<ServiceOfferingDocumentKind, HTMLInputElement | null>>>(
    {}
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await listServiceOfferingDocuments({ offering_id: offeringId, sign: true });
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setDocuments(res.data.documents);
    setIsInherited(res.data.is_inherited);
    setPublisherOrgName(res.data.publisher_org_name);
  }, [offeringId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleUpload(kind: ServiceOfferingDocumentKind, file: File) {
    setBusyKind(kind);
    setError(null);
    const res = await uploadServiceOfferingDocument({ offeringId, kind, file });
    setBusyKind(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await reload();
  }

  async function handleDelete(doc: ServiceOfferingDocument) {
    if (!window.confirm(`Remove ${SERVICE_OFFERING_DOCUMENT_KIND_LABELS[doc.kind]} (${doc.filename})?`)) {
      return;
    }
    setDeleteBusyId(doc.id);
    setError(null);
    const res = await deleteServiceOfferingDocument(doc.id);
    setDeleteBusyId(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await reload();
  }

  const docByKind = new Map(documents.map((d) => [d.kind, d]));

  const body = (
    <>
      {isInherited && publisherOrgName ? (
        <p className="mb-2 text-xs text-gray-500 dark:text-zinc-400">
          Files published by <strong>{publisherOrgName}</strong> — upload in the publisher org
          Service setup tab.
        </p>
      ) : null}

      {error ? (
        <p className="mb-2 text-sm text-red-700 dark:text-red-200">{error}</p>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading attachments…</p>
      ) : (
        <ul className="space-y-3">
          {UPLOAD_KINDS.map((kind) => {
            const doc = docByKind.get(kind);
            const busy = busyKind === kind;
            return (
              <li
                key={kind}
                className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/50"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                      {SERVICE_OFFERING_DOCUMENT_KIND_LABELS[kind]}
                      {offeringDocumentPublisherSuffix(publisherOrgName, isInherited)}
                    </div>
                    {doc ? (
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400 truncate">
                        {doc.filename} · {formatOfferingDocumentSize(doc.byte_size)}
                      </p>
                    ) : (
                      <p className="mt-0.5 text-xs text-gray-400 dark:text-zinc-500">No file yet</p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {doc?.signed_url ? (
                      <>
                        <a
                          href={doc.signed_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
                        >
                          Download
                        </a>
                        <button
                          type="button"
                          className="text-xs font-medium text-gray-600 hover:underline dark:text-zinc-300"
                          onClick={() => {
                            if (doc.signed_url) window.open(doc.signed_url, "_blank", "noopener");
                          }}
                        >
                          Print
                        </button>
                      </>
                    ) : null}
                    {!readOnly && !isInherited ? (
                      <>
                        <input
                          ref={(el) => {
                            fileInputs.current[kind] = el;
                          }}
                          type="file"
                          accept={SERVICE_OFFERING_DOCUMENT_ACCEPT}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (file) void handleUpload(kind, file);
                          }}
                        />
                        <button
                          type="button"
                          className={ghostBtnClass}
                          disabled={busy || deleteBusyId !== null}
                          onClick={() => fileInputs.current[kind]?.click()}
                        >
                          {busy ? "Uploading…" : doc ? "Replace" : "Upload"}
                        </button>
                        {doc ? (
                          <button
                            type="button"
                            className="text-xs text-red-700 hover:underline dark:text-red-300"
                            disabled={deleteBusyId === doc.id || busy}
                            onClick={() => void handleDelete(doc)}
                          >
                            {deleteBusyId === doc.id ? "Removing…" : "Remove"}
                          </button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && !isInherited ? (
        <p className="mt-3 text-xs text-gray-500 dark:text-zinc-400">
          PDF, DOCX, or XLSX up to 25 MB. Replacing a file keeps the prior version out of the active
          list (audit retention in a later slice).
        </p>
      ) : null}

      <div className="mt-2 flex justify-end">
        <button type="button" className={ghostBtnClass} onClick={() => void reload()} disabled={loading}>
          Refresh files
        </button>
      </div>
    </>
  );

  if (presentation === "card") {
    return (
      <SectionCard
        title="Pricing & guidance"
        description={`Order forms, pricing worksheets, and guidance PDFs for ${offeringLabel}`}
      >
        {body}
      </SectionCard>
    );
  }

  return (
    <ServiceCollapsibleSection
      title="Pricing & guidance forms"
      subtitle={`Order forms, pricing worksheets, and guidance PDFs for ${offeringLabel}`}
      defaultOpen={false}
    >
      {body}
    </ServiceCollapsibleSection>
  );
}

export function ServiceOfferingDocumentsReadOnlyLinks(props: {
  offeringId: string;
  compact?: boolean;
}) {
  const { offeringId, compact = false } = props;
  const [documents, setDocuments] = useState<ServiceOfferingDocument[]>([]);
  const [publisherOrgName, setPublisherOrgName] = useState<string | null>(null);
  const [isInherited, setIsInherited] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await listServiceOfferingDocuments({ offering_id: offeringId, sign: true });
      if (cancelled) return;
      setLoading(false);
      if (res.ok) {
        setDocuments(res.data.documents);
        setPublisherOrgName(res.data.publisher_org_name);
        setIsInherited(res.data.is_inherited);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offeringId]);

  if (loading) {
    return compact ? null : (
      <span className="text-xs text-gray-400 dark:text-zinc-500">Loading forms…</span>
    );
  }
  if (documents.length === 0) return null;

  return (
    <div className={compact ? "flex flex-wrap gap-x-3 gap-y-1" : "mt-2 space-y-1"}>
      {documents.map((doc) => (
        <span key={doc.id} className="inline-flex items-center gap-1 text-xs">
          <span className="text-gray-500 dark:text-zinc-400">
            {offeringDocumentLinkLabel(doc, { publisherOrgName, isInherited, compact: true })}:
          </span>
          {doc.signed_url ? (
            <>
              <a
                href={doc.signed_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-700 hover:underline dark:text-blue-300"
              >
                {compact ? "Download" : doc.filename}
              </a>
              <button
                type="button"
                className="text-gray-500 hover:underline dark:text-zinc-400"
                onClick={() => {
                  if (doc.signed_url) window.open(doc.signed_url, "_blank", "noopener");
                }}
              >
                Print
              </button>
            </>
          ) : (
            <span className="text-gray-400">{doc.filename}</span>
          )}
        </span>
      ))}
    </div>
  );
}
